import { config } from "../config";
import { callGateway, callGatewayBytes, type CallerIdentity } from "./gateway";
import { isNoRecords } from "./issuedBilling";

/**
 * Invoices from Ankh: what AnkhOS billed the provider the console is working
 * in. Read-only.
 *
 *   GET /billing/{ns}/invoices                   radbilling GET /invoiceapi/io.ankhos/{acct}
 *   GET /billing/{ns}/invoices/{InvoiceID}       radbilling GET /invoiceapi/io.ankhos/{acct}/{id}
 *   GET /billing/{ns}/invoices/{InvoiceID}/pdf   radbilling GET /invoiceapi/io.ankhos/{acct}/{id}/pdf
 *
 * 🔑 {ns} IS THE BUYER, NOT THE SELLER. radaccess (ProxyToBilling) rewrites the
 * path to the seller namespace io.ankhos and the provider's own account, so the
 * caller names neither and cannot ask for another provider's bill. These are
 * NOT the invoices a provider issues to its own customers -- that ledger is
 * /billing/{ns}/unpaidinvoices and /accounts, used by Record payment.
 *
 *   GET /billing/{ns}/receipts                   radbilling GET /receiptapi/io.ankhos/{acct}
 *   GET /billing/{ns}/receipts/{OrderID}/pdf     radbilling GET /receiptapi/io.ankhos/{acct}/{order}/pdf
 *
 * Grant: ankhbilling:read -- superadmin and Operator -- at radaccess v1.68.
 * The nav hides the screen from every other role for exactly that reason.
 */

/**
 * What became of a document's email, from radbilling's outbox (v0.1.95+; absent
 * when nothing was queued or radbilling predates it). Sent = the mail provider
 * accepted it, not proof it reached the inbox.
 */
export interface Delivery {
  /** Pending | Claimed | Sent | Failed | Abandoned */
  status: string;
  attempts: number;
  sentto?: string;
  detail?: string;
  updatedat: string;
}

/** One invoice as radbilling model.Invoice serialises it; the fields shown. */
export interface AnkhInvoice {
  id: string;
  invoicenumber?: string;
  documentheading: string;
  periodstart: string;
  periodend: string;
  currency: string;
  supplierlegalname: string;
  suppliergstin: string;
  buyername: string;
  buyergstin?: string;
  placeofsupply: string;
  taxablevalue: number;
  totalcgst: number;
  totalsgst: number;
  totaligst: number;
  totaltax: number;
  totalamount: number;
  totalamountinwords: string;
  syncstatus: string;
  createdat: string;
  lineitems?: AnkhInvoiceLine[];
  delivery?: Delivery;
}

export interface AnkhInvoiceLine {
  id: string;
  description: string;
  hsnsac?: string;
  quantity: number;
  unitprice: number;
  discountamount?: number;
  taxablevalue: number;
  gstpercent: number;
  taxamount: number;
  totalamount: number;
}

const enc = encodeURIComponent;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The gateway carries an invoice id through only if it parses as a UUID
 * (billingPathFor). Refuse anything else here too, so a malformed id never
 * leaves the browser as a request.
 */
function invoicePath(ns: string, invoiceId: string): string {
  if (!UUID.test(invoiceId)) throw new Error(`Not an invoice id: ${invoiceId}`);
  return `/billing/${enc(ns)}/invoices/${invoiceId}`;
}

/**
 * Newest first. radbilling answers 404 " Records Not Found" when Ankh has billed
 * none (model.ListInvoices) -- shown as an empty list, not as "Not found".
 */
export async function listAnkhInvoices(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<AnkhInvoice[]> {
  try {
    const r = await callGateway<AnkhInvoice[] | null>(
      identity,
      config().gatewayBase,
      `/billing/${enc(ns)}/invoices`,
      signal ? { signal } : {},
    );
    return [...(r.data ?? [])].sort((a, b) => b.createdat.localeCompare(a.createdat));
  } catch (e) {
    if (isNoRecords(e)) return [];
    throw e;
  }
}

export async function getAnkhInvoice(identity: CallerIdentity, ns: string, invoiceId: string, signal?: AbortSignal): Promise<AnkhInvoice> {
  const r = await callGateway<AnkhInvoice | null>(identity, config().gatewayBase, invoicePath(ns, invoiceId), signal ? { signal } : {});
  if (!r.data) throw new Error("The invoice answered with no body.");
  return r.data;
}

/** The printable PDF. radbilling answers 409 for a Draft, which has no number yet. */
export async function getAnkhInvoicePdf(identity: CallerIdentity, ns: string, invoiceId: string): Promise<Blob> {
  return callGatewayBytes(identity, config().gatewayBase, `${invoicePath(ns, invoiceId)}/pdf`, { accept: "application/pdf" });
}

/** One receipt as radbilling model.Receipt serialises it (v0.1.74); the fields shown. */
export interface AnkhReceipt {
  id: string;
  receiptnumber: string;
  financialyear: string;
  /** The payment the receipt is for. Its PDF is keyed by THIS, not by id. */
  orderid: string;
  invoiceid?: string;
  invoicenumber?: string;
  paymentmethod: string;
  paymentreference?: string;
  amount: number;
  currency: string;
  receivedat: string;
  issuedat: string;
  /** Issued | Cancelled */
  status: string;
  delivery?: Delivery;
}

/** Newest first as radbilling answers them, Cancelled included; [] when none. */
export async function listAnkhReceipts(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<AnkhReceipt[]> {
  const r = await callGateway<AnkhReceipt[] | null>(
    identity,
    config().gatewayBase,
    `/billing/${enc(ns)}/receipts`,
    signal ? { signal } : {},
  );
  return r.data ?? [];
}

/** The printable receipt, by the ORDER id (the payment), as the gateway keys it. */
export async function getAnkhReceiptPdf(identity: CallerIdentity, ns: string, orderId: string): Promise<Blob> {
  if (!UUID.test(orderId)) throw new Error(`Not a payment id: ${orderId}`);
  return callGatewayBytes(identity, config().gatewayBase, `/billing/${enc(ns)}/receipts/${orderId}/pdf`, { accept: "application/pdf" });
}
